/**
 * Pure-function pricing engine. Given a list of course-objects in the cart and
 * the list of active promotions, returns a breakdown:
 *
 *   {
 *     subtotal,            // sum of base prices
 *     discountTotal,       // total saved
 *     total,               // final amount to pay
 *     lines: [{course, basePrice, discount, finalPrice, appliedPromos:[...]}],
 *     appliedPackages: [{promo, courses, fixed_price, originalSum, savings}],
 *     bulkApplied,         // {promo, percent, base, amount} or null
 *   }
 *
 * Stacking strategy (applied in this exact order):
 *   1) Packages: a package applies when ALL of its course_ids are present in
 *      the cart. The courses absorbed by the package are billed at the fixed
 *      price (no other discount applies to them). If multiple packages overlap,
 *      we apply packages greedily in order of biggest savings first, then
 *      remove their courses from the pool.
 *   2) Quantity offers ("2x99 €"): any N still-loose eligible courses for a
 *      closed price, repeating while enough remain. Reported as packages.
 *   3) Course discounts: for each loose course (not absorbed by any package),
 *      pick the largest applicable course discount (percent or fixed €).
 *   4) Bulk discount: among bulk promotions whose min_courses ≤ number of
 *      eligible loose courses, pick the best and apply to their subtotal.
 *
 * Every promotion declares an `applies_to` scope — "courses" (kind=final),
 * "workshops" (kind=normal) or "both" — and only looks at matching courses.
 * A missing scope, or a course of unknown kind, counts as "both".
 *
 * MUST stay in sync with backend/pricing.py, which recomputes all of this at
 * checkout and is the number actually charged.
 */

const inScope = (course, promo) => {
  const scope = promo?.applies_to || "both";
  if (scope === "both") return true;
  const kind = course?.kind;
  if (kind == null) return true;
  return kind === (scope === "courses" ? "final" : "normal");
};

const courseSavings = (price, promo) => {
  if (!promo || promo.kind !== "course") return 0;
  if (promo.course_discount_type === "percent") {
    return (price * (promo.course_discount_value || 0)) / 100;
  }
  return Math.min(price, promo.course_discount_value || 0);
};

export function computeCart(courses, promotions) {
  const subtotal = courses.reduce((acc, c) => acc + (Number(c.price) || 0), 0);

  // ---- 1) Packages (greedy by savings) ----
  const idSet = new Set(courses.map((c) => c.id));
  const idToCourse = Object.fromEntries(courses.map((c) => [c.id, c]));

  const eligiblePackages = (promotions || [])
    .filter((p) => p.kind === "package" && Array.isArray(p.course_ids) && p.course_ids.length >= 2)
    .filter((p) => p.course_ids.every((cid) => idSet.has(cid)))
    .filter((p) => p.course_ids.every((cid) => inScope(idToCourse[cid], p)))
    .map((p) => {
      const originalSum = p.course_ids.reduce(
        (acc, cid) => acc + (Number(idToCourse[cid]?.price) || 0),
        0,
      );
      return { promo: p, originalSum, savings: Math.max(0, originalSum - (p.fixed_price || 0)) };
    })
    .sort((a, b) => b.savings - a.savings);

  const absorbed = new Set();
  const appliedPackages = [];
  for (const pkg of eligiblePackages) {
    if (pkg.promo.course_ids.some((cid) => absorbed.has(cid))) continue;
    pkg.promo.course_ids.forEach((cid) => absorbed.add(cid));
    appliedPackages.push({
      promo: pkg.promo,
      courses: pkg.promo.course_ids.map((cid) => idToCourse[cid]),
      fixed_price: pkg.promo.fixed_price || 0,
      originalSum: pkg.originalSum,
      savings: pkg.savings,
    });
  }

  // ---- 2) Quantity offers ("N cursos por X €") ----
  // Biggest group first, most expensive courses first inside each group, and
  // never applied when the group would end up costing more than it saves.
  const quantityOffers = (promotions || [])
    .filter((p) => p.kind === "quantity" && (p.quantity_count || 0) >= 2 && p.fixed_price != null)
    .sort((a, b) => (b.quantity_count || 0) - (a.quantity_count || 0));

  for (const offer of quantityOffers) {
    const n = offer.quantity_count;
    const fixed = Number(offer.fixed_price) || 0;
    for (;;) {
      const pool = courses
        .filter((c) => !absorbed.has(c.id) && inScope(c, offer))
        .sort((a, b) => (Number(b.price) || 0) - (Number(a.price) || 0));
      if (pool.length < n) break;
      const group = pool.slice(0, n);
      const originalSum = group.reduce((acc, c) => acc + (Number(c.price) || 0), 0);
      if (originalSum <= fixed) break;
      group.forEach((c) => absorbed.add(c.id));
      appliedPackages.push({
        promo: offer,
        courses: group,
        fixed_price: fixed,
        originalSum,
        savings: originalSum - fixed,
      });
    }
  }

  // ---- 3) Per-course discounts on the loose pool ----
  const looseCourses = courses.filter((c) => !absorbed.has(c.id));
  const coursePromosById = {};
  for (const p of promotions || []) {
    if (p.kind !== "course" || !p.course_id) continue;
    if (!inScope(idToCourse[p.course_id], p)) continue;
    const existing = coursePromosById[p.course_id];
    const newSav = courseSavings(idToCourse[p.course_id]?.price || 0, p);
    const oldSav = existing ? courseSavings(idToCourse[p.course_id]?.price || 0, existing) : -1;
    if (newSav > oldSav) coursePromosById[p.course_id] = p;
  }

  const lines = looseCourses.map((c) => {
    const promo = coursePromosById[c.id];
    const basePrice = Number(c.price) || 0;
    const discount = promo ? courseSavings(basePrice, promo) : 0;
    return {
      course: c,
      basePrice,
      discount,
      finalPrice: Math.max(0, basePrice - discount),
      appliedPromos: promo ? [promo] : [],
    };
  });

  // ---- 4) Bulk discount on the eligible part of the loose subtotal ----
  const looseSubtotalAfterCoursePromos = lines.reduce((acc, l) => acc + l.finalPrice, 0);
  const scopedLines = (p) => lines.filter((l) => inScope(l.course, p));
  const scopedBase = (p) => scopedLines(p).reduce((acc, l) => acc + l.finalPrice, 0);
  const bulkCandidates = (promotions || []).filter(
    (p) =>
      p.kind === "bulk" &&
      (p.bulk_percent || 0) > 0 &&
      scopedLines(p).length >= (p.bulk_min_courses || 2),
  );
  bulkCandidates.sort((a, b) => scopedBase(b) * (b.bulk_percent || 0) - scopedBase(a) * (a.bulk_percent || 0));
  const bulkPromo = bulkCandidates[0] || null;
  let bulkApplied = null;
  if (bulkPromo) {
    const base = scopedBase(bulkPromo);
    bulkApplied = {
      promo: bulkPromo,
      percent: bulkPromo.bulk_percent,
      base,
      amount: (base * (bulkPromo.bulk_percent || 0)) / 100,
    };
  }

  const packageTotal = appliedPackages.reduce((acc, p) => acc + p.fixed_price, 0);
  const looseAfterBulk = looseSubtotalAfterCoursePromos - (bulkApplied?.amount || 0);
  const total = packageTotal + looseAfterBulk;

  return {
    subtotal,
    discountTotal: subtotal - total,
    total,
    lines,
    appliedPackages,
    bulkApplied,
  };
}
